<script setup lang="ts">
import { ROUTER_PATHS } from '@/constants/router/paths'
import { computed, onMounted, ref, watch } from 'vue'
import { useLoadingView } from '@/modules/miiaFw/composables/useLoadingView'
import { useUserAttributes } from '@/modules/miiaFw/composables/useUserAttributes'
import { offlineDataUpdateProcessHandler } from '@/modules/offlineDataUpload/processHandler'
import { useAdminDistrictsStore } from '@/stores/adminDistrictsStore'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { useDisasterStore } from '@/stores/disasterStore'
import { useNetworkStore } from '@/stores/networkStore'
import { bulkDeleteExpiredItemsFromIDB as inspectionResultIdbBulkDelete } from '@/accessors/indexedDB/disasterEmergencyInspectionResultIdb'
import { PhotoDB } from '@/accessors/indexedDB/disasterEmergencyPhotoIdb'
import { useRouter } from 'vue-router'
import DestinationLeafletMapComp from '@/components/inspection/common/DestinationLeafletMap.vue'
import MiiaFwAlertMessage from '@/components/miiafw/utils/MiiaFwAlertMessage.vue'
import type { LatLngExpression } from 'leaflet'
import CommonModal from '@/components/common/dialog/CommonModal.vue'
import CommonButton from '@/components/common/button/CommonButton.vue'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import { useInspectionResultQueryStore } from '@/stores/inspectionResultQueryStore'
import { MODAL_RESULT, type ModalResult } from '@/modules/miiaFw/types/components/commonModal'
import { useOfflineDataDownloadProcessStore } from '@/stores/offlineDataDownloadProcessStore'
import { getCount } from '@/libs/leaflet/indexedDB'
import { checkFirstOfTheDay } from '@/accessors/sessionStorage/inspectorList'
import { EMPTY_BUILDIND } from '@/types/accessor/api/emergencyInspectionResult/building/building'
import { useRouterUtils } from '@/router/useRouterUtils'

/** routerインスタンス */
const router = useRouter()

const leafletRef = ref<InstanceType<typeof DestinationLeafletMapComp> | null>(null) // TODO: composableに切り出して置き換え
const isBuilding = ref(false)
const selectedAssessmentId = ref('')

const { getUserAttributesFromStore } = useUserAttributes()
const loadingView = useLoadingView()
const disasterStore = useDisasterStore()
const commonModalStore = useCommonModalStore()
const networkStore = useNetworkStore()
const adminDistrictsStore = useAdminDistrictsStore()
const inspectionResultStore = useInspectionResultStore()
const inspectionResultQueryStore = useInspectionResultQueryStore()
const offlineDataDownloadProcessStore = useOfflineDataDownloadProcessStore()

/**
 * 調査画面へ遷移する
 * @param なし
 * @returns なし
 */
const moveToInspectionView = () => {
  const { transition } = useRouterUtils(router)
  // 建物選択状態を初期化するため空建物をStoreに設定
  inspectionResultStore.setBuilding(EMPTY_BUILDIND)
  transition(ROUTER_PATHS.inspection.root)
}

/**
 * 調査画面へ遷移する(調査編集)
 * @param なし
 * @returns なし
 */
const moveToInspectionViewEdit = async () => {
  const { transition } = useRouterUtils(router)
  if (leafletRef.value) {
    await inspectionResultStore.setIDBData(selectedAssessmentId.value)
  }

  transition(ROUTER_PATHS.inspection.root)
}

/**
 * 地図未ダウンロードかつオフラインかどうかの判定
 * @returns true 地図未ダウンロードかつオフライン, false 地図ダウンロード済またはオンライン
 */
const isPossibleInvestigate = computed(() => {
  return !offlineDataDownloadProcessStore.isDownloaded && !networkStore.getIsOnline
})

/** 行政区画APIのquery叩く関数
 * @param govCode string | undefined
 * @returns なし
 */
const getStreetList = async (govCode: string | undefined) => {
  await adminDistrictsStore.getStreetListAction({ govCode })
}

/** 災害APIのquery叩く関数
 * @param govCode 自治体コード
 * @returns なし
 */
const getDisasters = async (govCode: string | undefined) => {
  await disasterStore.fetchAndSetDisasters({ govCode })
}

/** onMountedで叩くAPIをまとめてハンドリングする
 * @param なし
 * @returns なし
 */
const mountProcessing = async (govCode: string | undefined) => {
  await Promise.all([getStreetList(govCode), getDisasters(govCode)]).catch(e => {
    console.error(e)
  })

  // オフライン用に調査実施結果を取得（災害IDが必要になるため、災害情報の取得後に実行）
  await inspectionResultQueryStore.fetchQueryState(
    disasterStore.disasterId,
    disasterStore.disaster?.govCode
  )
}

/**
 * オフラインデータアップロード状況エラーチェック
 * @param なし
 * @returns なし
 */
const validatedUploadStatus = () => {
  const progressUpload = window.localStorage.getItem('dataUploadInProgress')
  // オフラインデータアップロード中に画面遷移した場合
  if (progressUpload != null) {
    commonModalStore
      .openConfirmModal({
        title: 'アップロード処理が中断されました',
        body: 'アップロード処理が途中で中断されました。<br>アップロード処理を再開しますか？',
        okTitle: 'はい',
        cancelTitle: 'いいえ'
      })
      .then(async (result: ModalResult) => {
        if (result === MODAL_RESULT.OK) {
          await networkStore.setOnlineStatus()
          await offlineDataUpdateProcessHandler()
        }
      })
      .catch(async (result: ModalResult) => {
        if (result === MODAL_RESULT.CANCEL) {
          await networkStore.setOnlineStatus()
        }
      })
  }
}

/**
 * オフライン地図が未ダウンロードか判定
 * @param なし
 * @returns なし
 */
const validatedOfflineMapNotDownload = async () => {
  const count = await getCount('downloadedFeature')
  if (count && count > 0) {
    offlineDataDownloadProcessStore.updateIsDownloaded(true)
  }
  if (
    !offlineDataDownloadProcessStore.isDownloaded &&
    !offlineDataDownloadProcessStore.isOpenAlertModal
  ) {
    commonModalStore
      .openAlertModal({
        title: 'オフライン地図が未ダウンロードです',
        body: 'オフライン地図が未ダウンロードです。ネットワークがないまたは不安定な場合に、オフライン機能での調査が行えません。<br>調査地域のネットワークが不安定だと予測される場合は地図を事前に必ずダウンロードしてください',
        okTitle: 'はい'
      })
      .then(() => {
        offlineDataDownloadProcessStore.updateIsOpenAlertModal(true)
      })
  }
}

const handleIsBuildingChange = (value: boolean) => {
  isBuilding.value = value
}

const updateSelectedAssessmentId = (value: string) => {
  selectedAssessmentId.value = value
}

// ライフサイクル
onMounted(async () => {
  loadingView.setLoadingView(true)
  // 調査実施結果Storeをリセット
  inspectionResultStore.resetAll()

  // IndexedDBに保存されている有効期限切れの調査データを削除する
  await inspectionResultIdbBulkDelete()
  const photoDB = PhotoDB.instance
  await photoDB.bulkDeleteExpiredItemsFromIDB()
  // その日のはじめに調査員情報のセッションデータを削除する
  checkFirstOfTheDay()

  // オフライン用の事前データを取得する
  const attribute = await getUserAttributesFromStore()
  await mountProcessing(attribute.attribute['disaster-commonattr'].govCode)

  // オフラインデータのアップロード状況エラーチェック
  validatedUploadStatus()
  // オフライン地図が未ダウンロードか判定
  validatedOfflineMapNotDownload()

  // マップ表示
  if (leafletRef.value) {
    const street = adminDistrictsStore.getStreetResult
    const location = [street[0].location[1], street[0].location[0]] as LatLngExpression
    await leafletRef.value.createMap(location, undefined, street[0].govCode)

    // 判定済家屋表示処理
    leafletRef.value.drawJudgedBuildingsForHome()
  }
  loadingView.setLoadingView(false)
})

// 判定済家屋監視
watch(
  () => inspectionResultQueryStore.getStateAll,
  () => {
    if (!leafletRef.value) return
    leafletRef.value.drawJudgedBuildingsForHome()
  },
  { deep: true }
)
</script>

<template>
  <div class="stickey-area">
    <MiiaFwAlertMessage ref="alertMessageRef"></MiiaFwAlertMessage>
    <DestinationLeafletMapComp
      ref="leafletRef"
      :idName="'inspectionHome'"
      @update:isBuilding="handleIsBuildingChange"
      @update:selectedAssessmentId="updateSelectedAssessmentId"
    />
    <CommonModal />
  </div>
  <div class="button-area d-flex justify-content-center my-4">
    <CommonButton
      variant="primary"
      :disabled="isPossibleInvestigate"
      class="inspection-btn new-inspection-btn shadow fs-4"
      @click="moveToInspectionView"
      >新規調査</CommonButton
    >
    <div style="width: 8rem"></div>
    <CommonButton
      variant="warning"
      :disabled="!isBuilding"
      class="inspection-btn text-black shadow fs-4"
      @click="moveToInspectionViewEdit()"
    >
      調査編集
    </CommonButton>
  </div>

  <div
    v-if="isPossibleInvestigate"
    class="text-center align-items-center mt-5 py-4 fs-5 bottom-text-content"
  >
    地図が未ダウンロードのため、オフライン状態で新規調査ができません
  </div>
</template>

<style scoped>
.inspection-btn {
  padding: 2rem 4rem;
}

.new-inspection-btn {
  background-color: #2c52cf !important;
}

.bottom-text-content {
  background-color: yellow;
}
</style>
